import { head,isEmpty } from 'lodash-es';

import { ChannelStore } from '../store/ChannelStore';

/**
 * Get the channel ID from the current URL query parameters.
 * @returns {String}
 */
export function getChannelIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('channelid') || urlParams.get('channelID') || '';
}

/**
 * Get the preselected timeseries IDs from the current URL query parameters.
 * @returns {String[]} unique IDs, empty when none are present
 */
export function getTimeseriesIdsFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const ids = [...urlParams.getAll('tsId'), ...urlParams.getAll('tsid')].filter(
        (id) => !isEmpty(id),
    );
    return [...new Set(ids)];
}

/**
 * Get the invalidation period from the current URL query parameters.
 * @returns {String|undefined}
 */
export function getInvalidationPeriodFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('invalidationPeriod') || undefined;
}

/**
 * Fetch channel metadata
 * @param {String} channelId
 * @returns {Promise<Object>}
 */
export async function fetchChannelMetaData(channelId) {
    return ChannelStore.getChannelMetaData(channelId);
}

/**
 * Fetch timeseries metadata for a channel
 * @param {String} channelId
 * @returns {Promise<Array>}
 */
export async function fetchTimeseriesMeta(channelId) {
    return ChannelStore.getTimeseriesMeta(channelId);
}

/**
 * Fetch last values for all timeseries in a channel
 * @param {String} channelId
 * @returns {Promise<Array>}
 */
export async function fetchLastValues(channelId) {
    return ChannelStore.getLastValues(channelId);
}

/**
 * Group timeseries by location and merge in last values
 * @param {Array} timeseriesMeta
 * @param {Array} lastValues
 * @returns {Object} Map of location name to timeseries array
 */
export function groupByLocation(timeseriesMeta, lastValues) {
    const lastValueMap = new Map();
    if (Array.isArray(lastValues)) {
        for (const lv of lastValues) {
            lastValueMap.set(lv.tsId ?? lv.id, lv);
        }
    }

    const groups = {};
    for (const ts of timeseriesMeta) {
        const location = ts.locationName || ts.location?.name || 'Unknown';
        if (!groups[location]) {
            groups[location] = [];
        }

        const lastValue = lastValueMap.get(ts.id);
        const dataPoint = !isEmpty(head(lastValue?.data)) ? head(lastValue.data) : null;
        groups[location].push({
            ...ts,
            latestValue: dataPoint?.[1] ?? lastValue?.value ?? null,
            dataTo: dataPoint?.[0] ?? lastValue?.timestamp ?? ts.dataTo ?? null,
        });
    }

    return groups;
}
